/**
 * 팀 역할 확인 헬퍼 함수
 * 
 * 도메인 규칙:
 * - 한 팀에서 같은 profile_id는 MANAGER 또는 MAKER 중 하나만 가질 수 있음
 * - 팀 역할과 프로젝트 역할은 별개
 */

import { createBrowserClient } from '@/supabase'

interface Team {
  id: number
  manager_profile_id: string | null
}

/**
 * 팀 매니저 여부 확인
 * @param team 팀 정보
 * @param profileId 확인할 profile_id
 * @returns 매니저 여부
 */
export const isTeamManager = (team: Team, profileId: string): boolean => {
  return team.manager_profile_id === profileId
}

/**
 * 팀 메이커(활성 멤버) 여부 확인
 * @param teamId 팀 ID
 * @param profileId 확인할 profile_id
 * @returns 활성 멤버 여부
 */
export const isActiveTeamMember = async (
  teamId: number, 
  profileId: string
): Promise<boolean> => {
  const supabase = createBrowserClient()
  const { data } = await supabase
    .from('team_members')
    .select('id')
    .eq('team_id', teamId)
    .eq('profile_id', profileId)
    .eq('status', 'active')
    .maybeSingle()
  return !!data
}

/**
 * 팀에서의 전체 역할 확인 (매니저 또는 메이커)
 * @param team 팀 정보
 * @param profileId 확인할 profile_id
 * @returns 'MANAGER' | 'MAKER' | null
 */
export const getTeamRoleForProfile = async (
  team: Team,
  profileId: string
): Promise<'MANAGER' | 'MAKER' | null> => {
  // 매니저 확인 (동기)
  if (team.manager_profile_id === profileId) {
    return 'MANAGER'
  }
  
  // 메이커 확인 (비동기)
  const isMember = await isActiveTeamMember(team.id, profileId)
  return isMember ? 'MAKER' : null
}

/**
 * 프로젝트에서의 역할 확인
 * 
 * @deprecated estimates 기반으로 변경됨.
 * 새로운 로직: estimates 테이블에서 ACCEPTED 상태인 견적서를 조회하여 역할 확인
 * 
 * @param counselId 프로젝트(counsel) ID
 * @param profileId 확인할 profile_id
 * @returns 'MANAGER' | 'MAKER' | null
 */
export const getProjectRole = async (
  counselId: number,
  profileId: string
): Promise<'MAKER' | 'MANAGER' | null> => {
  const supabase = createBrowserClient()
  
  // estimates 기반으로 변경: ACCEPTED 상태인 견적서 조회
  const { data: estimate } = await supabase
    .from('estimates')
    .select('proposer_type, proposer_team_id, proposer_profile_id, submitted_by_profile_id')
    .eq('counsel_id', counselId)
    .eq('status', 'ACCEPTED')
    .or(`submitted_by_profile_id.eq.${profileId},proposer_profile_id.eq.${profileId}`)
    .is('withdrawn_at', null)
    .maybeSingle()
  
  if (!estimate) {
    return null
  }
  
  // 팀 프로젝트인 경우 팀에서의 역할 확인
  if (estimate.proposer_type === 'TEAM' && estimate.proposer_team_id) {
    const { data: team } = await supabase
      .from('teams')
      .select('manager_profile_id')
      .eq('id', estimate.proposer_team_id)
      .maybeSingle()
    
    if (team?.manager_profile_id === profileId) {
      return 'MANAGER'
    }
    
    // 팀 멤버인지 확인
    const { data: teamMember } = await supabase
      .from('team_members')
      .select('role')
      .eq('team_id', estimate.proposer_team_id)
      .eq('profile_id', profileId)
      .eq('status', 'active')
      .maybeSingle()
    
    if (teamMember) {
      // OWNER/MANAGER 역할이면 MANAGER, 그 외는 MAKER
      return teamMember.role === 'OWNER' || teamMember.role === 'MANAGER' ? 'MANAGER' : 'MAKER'
    }
  }
  
  // 개인 프로젝트 또는 기본값
  return 'MAKER'
}

/**
 * 팀원의 계정 정보 조회 (profile_id 우선)
 * @param member team_members 레코드
 * @returns 계정 정보 또는 null
 */
export const getTeamMemberAccount = async (
  member: { profile_id?: string | null; maker_id?: string | null }
): Promise<{ profile_id: string; user_id: string; username: string; bio?: string | null } | null> => {
  const supabase = createBrowserClient()
  
  // profile_id가 있으면 profile_id로 조회 (우선)
  if (member.profile_id) {
    const { data, error } = await supabase
      .from('accounts')
      .select('profile_id, user_id, username, bio')
      .eq('profile_id', member.profile_id)
      .is('deleted_at', null)
      .maybeSingle()
    
    if (error) {
      console.warn('팀원 계정 조회 실패 (profile_id):', { error, profile_id: member.profile_id })
      return null
    }
    
    if (data) {
      return data
    }
  }
  
  // profile_id가 없고 maker_id가 있으면 maker_id로 조회 (하위 호환성)
  if (!member.profile_id && member.maker_id) {
    const { data, error } = await supabase
      .from('accounts')
      .select('profile_id, user_id, username, bio')
      .eq('user_id', member.maker_id)
      .is('deleted_at', null)
      .maybeSingle()
    
    if (error) {
      console.warn('팀원 계정 조회 실패 (maker_id):', { error, maker_id: member.maker_id })
      return null
    }
    
    return data || null
  }
  
  return null
}

/**
 * 팀 역할을 UI 표시용 라벨로 변환
 * DB 값(OWNER/MANAGER/MEMBER)을 사용자 친화적인 용어(매니저/메이커)로 매핑
 * 
 * @param role DB의 role 값 (OWNER, MANAGER, MEMBER 등)
 * @returns UI 표시용 라벨 ('매니저' | '메이커')
 */
export const toTeamRoleLabel = (role: string | null | undefined): '매니저' | '메이커' => {
  if (!role) return '메이커'
  
  const roleUpper = role.toUpperCase()
  
  // OWNER 또는 MANAGER → 매니저
  if (roleUpper === 'OWNER' || roleUpper === 'MANAGER') {
    return '매니저'
  }
  
  // 그 외 (MEMBER 등) → 메이커
  return '메이커'
}

/**
 * 팀 역할이 매니저 역할인지 확인
 * DB 값(OWNER/MANAGER)을 매니저로 판정
 * 
 * @param role DB의 role 값 (OWNER, MANAGER, MEMBER 등)
 * @returns 매니저 여부
 */
export const isTeamManagerRole = (role: string | null | undefined): boolean => {
  if (!role) return false
  
  const roleUpper = role.toUpperCase()
  
  // OWNER 또는 MANAGER → 매니저
  return roleUpper === 'OWNER' || roleUpper === 'MANAGER'
}

